import { useEffect, useMemo, useState } from 'react';
import { XIcon } from '@/components/icons';
import { getLabel } from '@/i18n/maps';
import type { FeedbackApi, FeedbackReason } from '@/api/feedback';
import type { Locale } from '@/types/widget';

const COMMENT_MAX = 2000;

export interface FeedbackReasonDialogProps {
  /** Backend message id the reason applies to. Dialog is a no-op when null. */
  messageBackendId: string | null;
  sessionId: string | null;
  api: FeedbackApi;
  reasons: FeedbackReason[];
  locale: Locale;
  onClose: () => void;
  /**
   * Serialize this submit onto the same per-message chain the button uses,
   * so a fast Envoyer click can't overtake the initial "no-reason down"
   * PUT and get its reason wiped by the late arrival.
   */
  enqueueWrite: <T>(messageId: string, fn: () => Promise<T>) => Promise<T>;
}

/**
 * Modal shown after the "no-reason down" PUT — lets the user add a reason
 * chip + optional free-text comment, then fires the follow-up PUT that
 * upserts the same feedback record. Closing without submitting keeps the
 * no-reason down (per spec §1: "第一步就已经落库了").
 *
 * Backdrop click / X / Esc all just close — none of them retract the
 * initial down. Submit is disabled until a chip is picked (and until
 * comment is non-empty for chips with `requires_comment`).
 */
export function FeedbackReasonDialog({
  messageBackendId,
  sessionId,
  api,
  reasons,
  locale,
  onClose,
  enqueueWrite,
}: FeedbackReasonDialogProps): JSX.Element | null {
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  // Reset dialog state whenever it opens for a new message
  useEffect(() => {
    if (messageBackendId) {
      setSelectedCode(null);
      setComment('');
      setSubmitting(false);
      setError(false);
    }
  }, [messageBackendId]);

  // Esc to close
  useEffect(() => {
    if (!messageBackendId) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [messageBackendId, onClose]);

  const selectedReason = useMemo(
    () => reasons.find((r) => r.code === selectedCode) ?? null,
    [reasons, selectedCode],
  );

  if (!messageBackendId || !sessionId) return null;

  const title = getLabel('feedback_dialog_title', locale);
  const commentPlaceholder = getLabel('feedback_comment_placeholder', locale);
  const submitLabel = getLabel('feedback_submit', locale);
  const errorLabel = getLabel('feedback_submit_error', locale);
  const closeLabel = getLabel('feedback_close', locale);

  const commentRequired = selectedReason?.requires_comment ?? false;
  const commentValid = !commentRequired || comment.trim().length > 0;
  const canSubmit = !!selectedCode && commentValid && !submitting;

  async function submit() {
    if (!canSubmit || !selectedCode) return;
    setSubmitting(true);
    setError(false);
    try {
      await enqueueWrite(messageBackendId!, () =>
        api.submit(messageBackendId!, {
          session_id: sessionId!,
          rating: 'down',
          reason_code: selectedCode,
          comment: comment.trim() || undefined,
        }),
      );
      onClose();
    } catch {
      setError(true);
      setSubmitting(false);
    }
  }

  return (
    <div className="calebasse-chatbot-root cal-fragment">
    <div
      className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-p-4"
      style={{ zIndex: 46 }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="tw-w-full tw-max-w-[560px] tw-bg-white tw-rounded-2xl tw-shadow-[0_24px_64px_rgba(0,0,0,0.28)] tw-flex tw-flex-col tw-overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="tw-flex tw-items-start tw-justify-between tw-px-6 tw-pt-6 tw-pb-5">
          <h2 className="tw-text-[16px] tw-font-semibold tw-text-cal-ink tw-leading-snug">
            {title}
          </h2>
          <button
            type="button"
            aria-label={closeLabel}
            onClick={onClose}
            className="tw-w-7 tw-h-7 tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-text-cal-ink-muted hover:tw-bg-cal-bg-soft hover:tw-text-cal-ink tw-transition-colors"
          >
            <XIcon width={14} height={14} />
          </button>
        </header>

        <div className="tw-px-6 tw-pb-6 tw-flex tw-flex-col tw-gap-5">
          <div className="tw-flex tw-flex-wrap tw-gap-2.5">
            {reasons.map((r) => {
              const active = selectedCode === r.code;
              return (
                <button
                  key={r.code}
                  type="button"
                  onClick={() => setSelectedCode(r.code)}
                  className={`tw-text-[13px] tw-leading-none tw-px-3.5 tw-py-2.5 tw-rounded-full tw-border tw-transition-colors ${
                    active
                      ? 'tw-bg-cal-ink tw-border-cal-ink tw-text-white'
                      : 'tw-bg-white tw-border-cal-border tw-text-cal-ink hover:tw-border-cal-ink-muted hover:tw-bg-cal-bg-soft'
                  }`}
                >
                  {r.label}
                </button>
              );
            })}
          </div>

          <div className="tw-relative">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX))}
              rows={4}
              placeholder={commentPlaceholder}
              aria-label={commentPlaceholder}
              className="tw-w-full tw-resize-none tw-text-[14px] tw-leading-relaxed tw-text-cal-ink tw-bg-white tw-border tw-border-cal-border tw-rounded-xl tw-px-4 tw-py-3 tw-outline-none focus:tw-border-cal-ink-muted tw-transition-colors placeholder:tw-text-cal-ink-muted"
            />
            <span className="tw-absolute tw-bottom-2 tw-right-3.5 tw-text-[10.5px] tw-text-cal-ink-muted tw-pointer-events-none tw-select-none">
              {comment.length}/{COMMENT_MAX}
            </span>
          </div>

          {commentRequired && (
            <p className="tw-text-[12px] tw-text-cal-ink-muted -tw-mt-3">
              {getLabel('feedback_comment_required', locale)}
            </p>
          )}

          {error && (
            <div className="tw-text-[12.5px] tw-text-red-700 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-md tw-px-3 tw-py-2">
              {errorLabel}
            </div>
          )}

          <div className="tw-flex tw-justify-end tw-pt-1">
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              className="tw-px-5 tw-py-2.5 tw-text-[13.5px] tw-font-semibold tw-text-white tw-bg-cal-ink hover:tw-bg-black tw-rounded-full tw-transition-colors disabled:tw-bg-cal-bg-soft disabled:tw-text-cal-ink-muted disabled:tw-cursor-not-allowed"
            >
              {submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}
